import React, { useState } from 'react';
import { Box, Button, ClickAwayListener, Drawer, makeStyles, Popper } from '@material-ui/core';
import CustomButton from 'components/CustomButtons/Button';
import { translate } from 'components/Translation';
import UserIcon from 'assets/img/icons/user.svg';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import styles from 'assets/jss/material-dashboard-pro-react/components/profileDetailsDropdownStyles';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import ChangePasswordDialog from 'views/Auth/ChangePasswordDialog';
import ChangeEmailDialog from 'views/Auth/ChangeEmail/ChangeEmailDialog';
import ChangeNumberDialog from 'views/Auth/ChangeNumber/ChangeNumberDialog';
import { AuthConsumer } from 'components/Auth/authContext';
import ProfileDetailsContent from './ProfileDetailsContent/ProfileDetailsContent';
import NotificationPanel from 'layouts/Dashboard/subcomponents/Notifications/NotificationPanel';
import bell from 'assets/img/icons/bell.svg';
import classNames from 'classnames';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IProfileDetailsDropDownProps {
	handleRouteClick: (navigationType: string, routeName: string, parentName?: string) => void;
}

const ProfileDetailsDropdown = ({ handleRouteClick }: IProfileDetailsDropDownProps) => {
	const isMobile = useMediaQuery('(max-width:991px)');
	const classes = useStyles();
	const [open, setOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [isChangePasswordShown, setIsChangePasswordShown] = useState<boolean>(false);
	const [isChangeNumberShown, setIsChangeNumberShown] = useState<boolean>(false);
	const [isChangeEmailShown, setIsChangeEmailShown] = useState<boolean>(false);
	const [isNotificationShown, setIsNotificationShown] = useState<boolean>(false);

	const hideChangePassword = () => {
		setIsChangePasswordShown(false);
	};

	const showChangePassword = () => {
		setIsChangePasswordShown(true);
		setOpen(false);
		handleRouteClick('secondary_navigation', 'change-password', translate('nav.user.profile'));
	};

	const hideNotification = () => {
		setIsNotificationShown(false);
	};

	const showNotification = () => {
		setIsNotificationShown(true);
		setOpen(false);
		handleRouteClick('secondary_navigation', 'show-notification', translate('nav.user.profile'));
	};

	const hideChangeNumber = () => {
		setIsChangeNumberShown(false);
	};

	const showChangeNumber = () => {
		setIsChangeNumberShown(true);
		setOpen(false);
		handleRouteClick('secondary_navigation', 'change-number', translate('nav.user.profile'));
	};

	const hideChangeEmail = () => {
		setIsChangeEmailShown(false);
	};

	const showChangeEmail = () => {
		setIsChangeEmailShown(true);
		setOpen(false);
		handleRouteClick('secondary_navigation', 'change-email', translate('nav.user.profile'));
	};

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
		setOpen((prev) => !prev);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			setAnchorEl(event.currentTarget);
			setOpen((prev) => !prev);
		} else if (event.key === 'Escape') {
			setOpen(false);
		}
	};

	const handleClickAway = () => {
		setOpen(false);
	};

	const popperClasses = classNames(classes.popper, {
		[classes.popperMobile]: isMobile,
	});

	return (
		<>
			<Box display={{ xs: 'inline', lg: 'none' }}>
				<CustomButton
					color="primary-dark-v2"
					noMargin
					onClick={() => {
						showNotification();
					}}
					onKeyDown={handleKeyDown}
					className={'accountButtonClasses'}
					aria-haspopup="true"
					aria-expanded={isNotificationShown}
					aria-controls="notifications-popover"
				>
					<img src={bell} height={24} width={24} alt="" role="presentation" />
				</CustomButton>
			</Box>
			<CustomButton
				color="primary-dark-v2"
				noMargin
				aria-label={translate('nav.user.profile')}
				onClick={(event: React.MouseEvent<HTMLElement>) => {
					handleClick(event);
					handleRouteClick('secondary_navigation', translate('nav.user.profile'));
				}}
				onKeyDown={handleKeyDown}
				className={'accountButtonClasses'}
				aria-haspopup="true"
				aria-expanded={open}
				aria-controls="user-profile-popover"
			>
				<img src={UserIcon} height={24} width={24} alt="" role="presentation" />
			</CustomButton>
			{isMobile ? (
				<Drawer
					className={classes.drawer}
					variant="temporary"
					classes={{
						paper: classes.drawerPaper,
					}}
					anchor="right"
					open={open}
				>
					<div>
						<Box className={classes.closeButton}>
							<Button onClick={() => setOpen(false)} aria-label={translate('close')}>
								<img src="/close.svg" alt="" role="presentation" />
							</Button>
						</Box>
						<ProfileDetailsContent
							classes={classes}
							showChangeEmail={showChangeEmail}
							showChangeNumber={showChangeNumber}
							showChangePassword={showChangePassword}
						/>
					</div>
				</Drawer>
			) : (
				<Popper
					open={open}
					anchorEl={anchorEl}
					disablePortal
					placement="bottom-end"
					role="dialog"
					className={popperClasses}
				>
					<ClickAwayListener onClickAway={handleClickAway}>
						<div>
							<ProfileDetailsContent
								classes={classes}
								showChangeEmail={showChangeEmail}
								showChangeNumber={showChangeNumber}
								showChangePassword={showChangePassword}
							/>
						</div>
					</ClickAwayListener>
				</Popper>
			)}
			<AuthConsumer>
				{({ user }) => (
					<>
						<ConditionalRender
							show={isChangePasswordShown}
							onTrue={() => (
								<ChangePasswordDialog
									hideChangePassword={hideChangePassword}
									msisdn={user.contactNumber1}
								/>
							)}
						/>

						<ConditionalRender
							show={isChangeEmailShown}
							onTrue={() => (
								<ChangeEmailDialog hideChangeEmail={hideChangeEmail} msisdn={user.contactNumber1} />
							)}
						/>

						<ConditionalRender
							show={isChangeNumberShown}
							onTrue={() => (
								<ChangeNumberDialog hideChangeNumber={hideChangeNumber} msisdn={user.contactNumber1} />
							)}
						/>
						<ConditionalRender
							show={isNotificationShown}
							onTrue={() => (
								<NotificationPanel show={isNotificationShown} hideNotification={hideNotification} />
							)}
						/>
					</>
				)}
			</AuthConsumer>
		</>
	);
};

export default ProfileDetailsDropdown;
